import { memo, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Table as TableType, Group } from '@/types'
import { getTableDisplayName } from '@/utils/normalizers'
import { isForeignKey } from '@/utils/columnKeys'
import { getTableHeight, getTableWidth } from '@/utils/geometry'
import { TableHeader } from './TableHeader'
import { TableColumn } from './TableColumn'
import { TableFooter } from './TableFooter'
import { useSelectionStore, useDiagramStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import { useTableDrag } from '@/hooks/useTableDrag'
import { useTableResize } from '@/hooks/useTableResize'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import { TableResizeHandles } from './TableResizeHandles'

const DEFAULT_GROUP_COLOR = '#94A3B8'

interface TableProps {
  table: TableType
  allTables: Pick<TableType, 'schema'>[]
  groups: Record<string, Group>
  isSelected: boolean
  isMultiSelected: boolean
  isRelSrc: boolean
  onTableClick?: (tableId: string, event: React.MouseEvent) => void
  onTableContextMenu?: (tableId: string, event: React.MouseEvent) => void
  onRequestEdit?: (tableId: string) => void
  onOpenFkPanel?: (tableId: string, event: React.MouseEvent<HTMLButtonElement>) => void
  onOpenIndexPanel?: (tableId: string, event: React.MouseEvent<HTMLButtonElement>) => void
}

export const Table = memo(function Table({
  table,
  allTables,
  groups,
  isSelected,
  isMultiSelected,
  isRelSrc,
  onTableClick,
  onTableContextMenu,
  onRequestEdit,
  onOpenFkPanel,
  onOpenIndexPanel,
}: TableProps) {
  const { t } = useTranslation()
  const readOnly = useDraftStore((state) => state.mode === 'view')
  const { isDragging, handleHeaderPointerDown, handleTableClickCapture } = useTableDrag(table.id)
  const { isResizing, startResize } = useTableResize(table.id)
  const group = table.g ? groups[table.g] : undefined
  const groupColor = group?.color ?? DEFAULT_GROUP_COLOR
  const showConfirm = useConfirmStore((state) => state.show)

  const handleDelete = useCallback(() => {
    if (readOnly) return
    showConfirm(
      t('prompts.deleteTableTitle'),
      t('contextMenu.deleteTableConfirm', { name: table.name }),
      () => {
        useDiagramStore.getState().deleteTable(table.id)
        useSelectionStore.getState().clearSelection()
      },
    )
  }, [readOnly, showConfirm, t, table.id, table.name])

  const displayName = useMemo(
    () => getTableDisplayName(table, allTables),
    [table, allTables]
  )

  const fkCount = useMemo(
    () => table.cols.filter((c) => isForeignKey(c)).length,
    [table.cols]
  )

  const idxCount = useMemo(
    () => (table.indexes ?? []).length,
    [table.indexes]
  )
  const tableWidth = getTableWidth(table)
  const tableHeight = getTableHeight(table)

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (onTableClick) {
        onTableClick(table.id, e)
        return
      }
      e.stopPropagation()
      useSelectionStore.getState().selectTable(table.id)
    },
    [onTableClick, table.id]
  )

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (onTableContextMenu) {
      onTableContextMenu(table.id, e)
      return
    }

    useSelectionStore.getState().selectTable(table.id)
  }, [onTableContextMenu, table.id])

  let boxShadow = '0 2px 12px rgba(0,0,0,.25), 0 0 0 1px rgba(99,102,241,.06)'
  if (isSelected) {
    boxShadow = '0 0 0 2.5px #4F46E5, 0 2px 12px rgba(0,0,0,.25), 0 0 24px rgba(99,102,241,.3)'
  } else if (isMultiSelected) {
    boxShadow = '0 0 0 2px #818CF8, 0 2px 12px rgba(0,0,0,.25), 0 0 16px rgba(99,102,241,.2)'
  } else if (isRelSrc) {
    boxShadow = '0 0 0 3px #6366F1, 0 0 24px rgba(99,102,241,.45)'
  }

  return (
    <div
      className="etb"
      id={`etb-${table.id}`}
      onClickCapture={handleTableClickCapture}
      onClick={handleClick}
      onDoubleClick={(event) => {
        if ((event.target as HTMLElement).closest('button')) return
        if (readOnly) return
        onRequestEdit?.(table.id)
      }}
      onContextMenu={handleContextMenu}
      style={{
        position: 'absolute',
        left: table.x,
        top: table.y,
        width: tableWidth,
        height: tableHeight,
        zIndex: isSelected || isMultiSelected || isRelSrc || isResizing ? 4 : 2,
        fontSize: 12,
        cursor: 'default',
      }}
    >
      <div
        className="etb-shell"
        style={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 8,
          boxShadow,
          transition: isResizing ? 'none' : 'box-shadow .2s',
        }}
      >
        <TableHeader
          displayName={displayName}
          groupColor={groupColor}
          tableId={table.id}
          isSelected={isSelected}
          isDragging={isDragging}
          editTitle={t('common.edit')}
          deleteTitle={t('common.delete')}
          readOnly={readOnly}
          onPointerDown={handleHeaderPointerDown}
          onDoubleClick={() => {
            if (!readOnly) onRequestEdit?.(table.id)
          }}
          onEdit={() => {
            if (!readOnly) onRequestEdit?.(table.id)
          }}
          onDelete={handleDelete}
        />

        <div className="ebody" style={{ background: 'var(--tbl-body)', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {table.cols.map((col, i) => (
            <TableColumn key={`${col.name}-${i}`} col={col} tableId={table.id} />
          ))}
        </div>

        <TableFooter
          tableId={table.id}
          hasColumns={table.cols.length > 0}
          fkCount={fkCount}
          idxCount={idxCount}
          fkTitle={t('details.foreignKeys')}
          idxTitle={t('details.indexes')}
          readOnly={readOnly}
          onOpenFk={readOnly ? undefined : onOpenFkPanel}
          onOpenIdx={readOnly ? undefined : onOpenIndexPanel}
        />
      </div>

      {isSelected && !isMultiSelected && !readOnly ? (
        <TableResizeHandles onPointerDown={startResize} />
      ) : null}
    </div>
  )
})
